import { useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { C } from "../../shared/theme";
import { pageColumn } from "../../shared/layout";
import { useDragSensors } from "../../shared/Sortable";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { intro } from "../../shared/textStyles";
import { Segmented } from "../../shared/Segmented";
import { useViewState } from "../../shared/useViewState";
import { ListRow, listCard } from "../../shared/ListRow";
import { useAddRequest } from "../../shared/useAddRequest";
import { AddButton } from "../../shared/AddButton";
import { PLAN_ITEM_KINDS } from "./planItems";
import { usePlanItems } from "./usePlanItems";
import { SortablePlanItem, DreamCard } from "./PlanItemCards";
import { PlanItemEditor } from "./PlanItemEditor";
import { TASK_LINKS } from "../tasks/taskLinks";
import { LinkedTasks } from "../tasks/LinkedTasks";
import { useTaskRows } from "../tasks/useTaskRows";

// The Dreamboard: photos, one column on a phone, a grid on desktop. Reached from Home's
// Dream tile; the first dream matters most. Each dream's tasks fold under its card (LinkedTasks): `tasks` and `goals`
// are the user's, and the task editor opens through TaskActionsContext.
// Photos shows each dream's card with its photo and tasks; Names folds them all to a list of names with ⠿, one column,
// to put them in order (Craig, 29 Oct 2026: "collapse all the cards to display only the name of each dream along with
// the drag handle to make it easier for the user to reorder them"). It shows once there are two dreams to put in
// order. The choice lasts for the session.
const PHOTOS = "photos", NAMES = "names";
const VIEWS = [[PHOTOS, "Photos"], [NAMES, "Names"]];
// One column on a phone, as many 300px columns as fit on a wider screen.
const GRID = "repeat(auto-fill, minmax(min(100%, 300px), 1fr))";

export function PlanItemsPage({ kind, uid, tasks, goals, onNav, addRequested, onAddHandled }) {
  const k = PLAN_ITEM_KINDS[kind];
  const link = TASK_LINKS[kind];
  const taskRows = useTaskRows(goals);
  const linkTasks = taskRows.linkTasks(tasks);
  const { items, loading, error, add, update, remove, move } = usePlanItems(uid, kind);
  const [editing, setEditing] = useState(null); // null, "new", or an item
  useAddRequest(addRequested, onAddHandled, () => setEditing("new"));
  const [moveError, setMoveError] = useState(false);
  const [viewState, updateViewState] = useViewState(`nudge-${kind}-ui-${uid}`);
  // Names needs two dreams; while they load, the saved choice sizes the skeleton.
  const view = viewState.view === NAMES && (loading || items.length > 1) ? NAMES : PHOTOS;
  const names = view === NAMES;
  const sensors = useDragSensors();
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setMoveError(false);
    move(active.id, over.id).catch(e => { console.error(`Reordering ${kind} failed:`, e); setMoveError(true); });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text, position: "relative" }}>
      {editing && (
        <PlanItemEditor kind={kind} item={editing === "new" ? null : editing}
          onSave={(fields) => (editing === "new" ? add(fields) : update(editing.id, fields))}
          onDelete={() => remove(editing.id)}
          onClose={() => setEditing(null)} />
      )}

      <TopBar title={k.title} help={HELP[kind]} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "4px 16px 24px" }}>

          {loading && (names ? (
            <div aria-hidden="true" style={{ ...pageColumn, marginTop: 20, height: 200, borderRadius: C.r, background: C.surface2 }} />
          ) : (
            <div aria-hidden="true" style={{ marginTop: 20, display: "grid", gap: 12, gridTemplateColumns: GRID }}>
              {[0, 1].map(i => <div key={i} style={{ height: 260, borderRadius: C.r, background: C.surface2 }} />)}
            </div>
          ))}

          {error && (
            <p role="alert" style={{ ...intro(), margin: "16px 4px 0" }}>
              We couldn't load your {k.many}. Check your connection, then open this page again.
            </p>
          )}

          {!loading && !error && items.length === 0 && (
            <div style={{ padding: "12px 4px 0", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16 }}>
              <p style={intro()}>{k.empty}</p>
              <AddButton onClick={() => setEditing("new")}>{`Add a ${k.one}`}</AddButton>
            </div>
          )}

          {items.length > 0 && (
            <>
              <div style={pageColumn}>
                <p style={{ ...intro(), margin: "8px 4px 12px" }}>{k.intro}</p>
                {items.length > 1 && (
                  <div style={{ margin: "0 0 16px" }}>
                    <Segmented label={`Show ${k.many} as`} stretch options={VIEWS} value={view} onChange={(v) => updateViewState({ view: v })} />
                  </div>
                )}
              </div>
              {moveError && (
                <p role="alert" style={{ ...intro(), color: C.red, margin: "0 4px 16px" }}>
                  We couldn't save the new order. Check your connection and drag it again.
                </p>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={items.map(it => it.id)} strategy={names ? verticalListSortingStrategy : rectSortingStrategy}>
                  {names ? (
                    <div style={{ ...listCard(), ...pageColumn }}>
                      {items.map((item, i) => (
                        <SortablePlanItem key={item.id} id={item.id} style={{ background: C.surface }}>
                          {(handle) => <ListRow text={item.title} first={i === 0} editLabel={`Edit ${k.one}`} onEdit={() => setEditing(item)} handle={handle(item.title)} />}
                        </SortablePlanItem>
                      ))}
                    </div>
                  ) : (
                  /* Each card is as tall as it needs, so a dream with its tasks open doesn't stretch its neighbours. */
                  <div style={{ display: "grid", gap: 12, gridTemplateColumns: GRID, alignItems: "start" }}>
                    {items.map(item => (
                      <SortablePlanItem key={item.id} id={item.id}>
                        {(handle) => <DreamCard item={item} handle={handle} onOpen={() => setEditing(item)}
                          footer={<LinkedTasks link={link} id={item.id} name={item.title} linkTasks={linkTasks} style={{ marginTop: -6 }} />} />}
                      </SortablePlanItem>
                    ))}
                  </div>
                  )}
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </div>
      {taskRows.toast}
      <BottomNav active={null} onNav={onNav} />
    </div>
  );
}
